import { useState, type ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, Dialog, EmptyState, Field, Input, Select, Spinner } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { PeriodControls, istanbulToday, usePeriod } from '../components/reports/period';
import { api, ApiError, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { bpsLabel, minorToInput, toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import { useStoreChoice } from '../lib/store-choice';
import type {
  ExpenseList,
  FeeRuleList,
  ShipmentCostImport,
  ShipmentCostList,
  ShippingCostRuleList,
} from '../lib/types-faz8';
import { percentToBps } from './pricing';

/**
 * Finans girdileri (F8-04, ADR-804): ödeme komisyonu kuralları, kargo maliyetleri (elle ve kargo faturası dosyası),
 * kargo maliyeti tahmin kuralları, işletme giderleri. Görmek finans raporları, değiştirmek finans girdilerini yönetme
 * yetkisi ister. Yöntemler: docs/faz-8/REPORTS_FINANCE_TR.md.
 */

const SECTIONS = ['fees', 'shipments', 'shippingRules', 'expenses'] as const;
type Section = (typeof SECTIONS)[number];
const PROVIDERS = ['iyzico', 'paytr', 'bank_transfer', 'cash_on_delivery', 'open_account', 'manual'] as const;
const CARRIERS = [
  'yurtici',
  'aras',
  'mng',
  'ptt',
  'surat',
  'hepsijet',
  'kolaygelsin',
  'ups',
  'other',
] as const;
const CATEGORIES = [
  'rent',
  'salary',
  'marketing',
  'software',
  'packaging',
  'logistics',
  'utilities',
  'professional',
  'tax_fees',
  'other',
] as const;

const th = 'px-3 py-2 text-start font-medium';
const thR = 'px-3 py-2 text-end font-medium';
const td = 'px-3 py-2';
const tdR = 'px-3 py-2 text-end tabular';

function Table({ label, head, children }: { label: string; head: ReactNode; children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-md border border-line">
      <table className="w-full min-w-[40rem] text-sm" aria-label={label}>
        <thead className="bg-surface-2 text-ink-2">{head}</thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function ErrorText({ error }: { error: unknown }) {
  return (
    <p role="alert" className="text-sm text-rose">
      {error instanceof ApiError ? error.title : String(error)}
    </p>
  );
}

export function FinanceInputsPage() {
  const t = useT();
  const { can } = usePermissions();
  const manage = can('reports.finance.manage');
  const [section, setSection] = useState<Section>('fees');
  return (
    <PageFrame helpKey="page.finance_inputs">
      <div className="flex flex-wrap items-end gap-4">
        <Field label={t('admin.financeInputs.section')}>
          {(p) => (
            <Select {...p} value={section} onChange={(e) => setSection(e.target.value as Section)}>
              {SECTIONS.map((s) => (
                <option key={s} value={s}>
                  {t(`admin.financeInputs.sections.${s}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      {!manage && <p className="text-sm text-ink-3">{t('admin.financeInputs.readOnly')}</p>}
      {section === 'fees' && <FeeRules manage={manage} />}
      {section === 'shipments' && <ShipmentCosts manage={manage} />}
      {section === 'shippingRules' && <ShippingRules manage={manage} />}
      {section === 'expenses' && <Expenses manage={manage} />}
    </PageFrame>
  );
}

// ------------------------------------------------------------------------------ Ödeme komisyonu kuralları

type FeeRule = FeeRuleList[number];

function FeeRules({ manage }: { manage: boolean }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const q = useQuery({
    queryKey: ['fee-rules'],
    queryFn: () => api.get<FeeRuleList>('/v1/admin/finance/fee-rules'),
  });
  const [editing, setEditing] = useState<FeeRule | 'new' | null>(null);
  const remove = useApiMutation((id: string) => api.del(`/v1/admin/finance/fee-rules/${id}`), {
    invalidate: [['fee-rules']],
    success: t('admin.financeInputs.feeRuleDeleted'),
  });
  const inst = (n: number | null) =>
    n === null
      ? t('admin.financeInputs.allInstallments')
      : n === 1
        ? t('admin.financeInputs.singlePayment')
        : t('admin.financeInputs.installmentN', { n });
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-semibold text-ink">{t('admin.financeInputs.sections.fees')}</h2>
        <HelpTip k="finance_inputs.fee_rule" />
        {manage && (
          <Button variant="primary" size="sm" className="ms-auto" onClick={() => setEditing('new')}>
            <Plus className="h-4 w-4" aria-hidden />
            {t('admin.financeInputs.addFeeRule')}
          </Button>
        )}
      </div>
      {q.isError ? (
        <ErrorText error={q.error} />
      ) : !q.data ? (
        <Spinner label={t('common.loading')} />
      ) : !q.data.length ? (
        <EmptyState
          title={t('admin.financeInputs.emptyFees')}
          description={t('admin.financeInputs.emptyFeesLead')}
        />
      ) : (
        <Table
          label={t('admin.financeInputs.sections.fees')}
          head={
            <tr>
              <th className={th}>{t('admin.financeInputs.columns.provider')}</th>
              <th className={th}>{t('admin.financeInputs.columns.installments')}</th>
              <th className={thR}>{t('admin.financeInputs.columns.rate')}</th>
              <th className={thR}>{t('admin.financeInputs.columns.fixedFee')}</th>
              <th className={th}>{t('admin.financeInputs.columns.validity')}</th>
              {manage && (
                <th className={th}>
                  <span className="sr-only">{t('common.actions')}</span>
                </th>
              )}
            </tr>
          }
        >
          {q.data.map((r) => (
            <tr key={r.id} className="border-t border-line">
              <td className={td}>
                {t(`admin.financeInputs.providers.${r.provider}`)}
                {r.note && <span className="block text-xs text-ink-3">{r.note}</span>}
              </td>
              <td className={td}>{inst(r.installmentCount)}</td>
              <td className={tdR}>{bpsLabel(r.rateBps)}</td>
              <td className={tdR}>{money(r.fixedFeeMinor, r.currency)}</td>
              <td className={td}>
                {formatDate(r.validFrom, false)} –{' '}
                {r.validTo ? formatDate(r.validTo, false) : t('admin.financeInputs.openEnded')}
              </td>
              {manage && (
                <td className={`${td} text-end`}>
                  <span className="inline-flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
                      {t('common.edit')}
                    </Button>
                    <ConfirmButton
                      message={t('admin.financeInputs.confirmDeleteFee')}
                      onConfirm={() => remove.mutateAsync(r.id)}
                    >
                      {t('common.delete')}
                    </ConfirmButton>
                  </span>
                </td>
              )}
            </tr>
          ))}
        </Table>
      )}
      {editing && (
        <FeeRuleDialog rule={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
    </section>
  );
}

const rateText = (bps: number) => {
  const s = (bps / 100).toFixed(2);
  return s.replace(/\.?0+$/, '').replace('.', ',');
};

function FeeRuleDialog({ rule, onClose }: { rule: FeeRule | null; onClose: () => void }) {
  const t = useT();
  const [provider, setProvider] = useState<string>(rule?.provider ?? 'iyzico');
  const [installments, setInstallments] = useState(
    rule?.installmentCount ? String(rule.installmentCount) : '',
  );
  const [rate, setRate] = useState(rule ? rateText(rule.rateBps) : '');
  const [fixed, setFixed] = useState(rule ? minorToInput(rule.fixedFeeMinor) : '');
  const [from, setFrom] = useState(rule?.validFrom ?? istanbulToday());
  const [to, setTo] = useState(rule?.validTo ?? '');
  const [note, setNote] = useState(rule?.note ?? '');
  const [idem] = useState(() => new IdempotencyKey());
  const bps = percentToBps(rate);
  const fixedMinor = fixed.trim() ? toMinor(fixed, 'TRY') : 0;
  const valid = bps !== null && bps >= 0 && bps <= 5000 && fixedMinor !== null;
  const save = useApiMutation(
    () => {
      const body = {
        installmentCount: installments ? Number(installments) : null,
        rateBps: bps!,
        fixedFeeMinor: fixedMinor!,
        validFrom: from,
        validTo: to || null,
        note: note.trim() || null,
      };
      return rule
        ? api.patch(`/v1/admin/finance/fee-rules/${rule.id}`, body, { ifMatch: rule.version })
        : api.post('/v1/admin/finance/fee-rules', { ...body, provider }, { idempotencyKey: idem });
    },
    {
      invalidate: [['fee-rules']],
      success: t('admin.financeInputs.feeRuleSaved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={rule ? t('admin.financeInputs.editFeeRule') : t('admin.financeInputs.addFeeRule')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!valid}
            onClick={() => save.mutate(undefined)}
          >
            {t('admin.financeInputs.saveFeeRule')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('admin.financeInputs.provider')} required>
          {(p) => (
            <Select {...p} value={provider} disabled={!!rule} onChange={(e) => setProvider(e.target.value)}>
              {PROVIDERS.map((x) => (
                <option key={x} value={x}>
                  {t(`admin.financeInputs.providers.${x}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label={t('admin.financeInputs.installments')}
          error={fieldError(save.error, 'installmentCount')}
        >
          {(p) => (
            <Select {...p} value={installments} onChange={(e) => setInstallments(e.target.value)}>
              <option value="">{t('admin.financeInputs.allInstallments')}</option>
              <option value="1">{t('admin.financeInputs.singlePayment')}</option>
              {[2, 3, 4, 5, 6, 9, 12].map((n) => (
                <option key={n} value={n}>
                  {t('admin.financeInputs.installmentN', { n })}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label={t('admin.financeInputs.rate')}
          required
          error={
            rate && bps === null ? t('admin.financeInputs.rateInvalid') : fieldError(save.error, 'rateBps')
          }
        >
          {(p) => <Input {...p} inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} />}
        </Field>
        <Field
          label={t('admin.financeInputs.fixedFee')}
          optionalLabel={t('common.optional')}
          error={fixed && fixedMinor === null ? t('admin.financeInputs.amountInvalid') : undefined}
        >
          {(p) => (
            <Input {...p} inputMode="decimal" value={fixed} onChange={(e) => setFixed(e.target.value)} />
          )}
        </Field>
        <Field
          label={t('admin.financeInputs.validFrom')}
          required
          error={fieldError(save.error, 'validFrom')}
        >
          {(p) => <Input {...p} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />}
        </Field>
        <Field
          label={t('admin.financeInputs.validTo')}
          optionalLabel={t('admin.financeInputs.openEnded')}
          error={fieldError(save.error, 'validTo')}
        >
          {(p) => <Input {...p} type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />}
        </Field>
        <div className="sm:col-span-2">
          <Field label={t('admin.financeInputs.note')} optionalLabel={t('common.optional')}>
            {(p) => <Input {...p} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />}
          </Field>
        </div>
      </div>
    </Dialog>
  );
}

// ------------------------------------------------------------------------------------ Kargo maliyetleri

type ShipRow = ShipmentCostList['rows'][number];

function ShipmentCosts({ manage }: { manage: boolean }) {
  const t = useT();
  const money = useMoney();
  const { formatDate, formatNumber } = useI18n();
  const period = usePeriod('this_month');
  const [filter, setFilter] = useState('all');
  const [carrier, setCarrier] = useState('');
  const [editing, setEditing] = useState<ShipRow | null>(null);
  const [importing, setImporting] = useState(false);
  const params = new URLSearchParams(period.params);
  params.delete('interval');
  params.set('filter', filter);
  if (carrier) params.set('carrier', carrier);
  const q = useQuery({
    queryKey: ['shipment-costs', params.toString()],
    queryFn: () => api.get<ShipmentCostList>(`/v1/admin/finance/shipment-costs?${params}`),
    placeholderData: keepPreviousData,
  });
  const remove = useApiMutation((id: string) => api.del(`/v1/admin/finance/shipment-costs/${id}`), {
    invalidate: [['shipment-costs']],
    success: t('admin.financeInputs.costDeleted'),
  });
  const tl = (n: number | null) => money(n, 'TRY');
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-semibold text-ink">{t('admin.financeInputs.sections.shipments')}</h2>
        <HelpTip k="finance_inputs.shipment_cost" />
        <span className="ms-auto inline-flex items-center gap-1">
          <Button size="sm" onClick={() => setImporting(true)}>
            {t('admin.financeInputs.import')}
          </Button>
          <HelpTip k="finance_inputs.import" />
        </span>
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <PeriodControls period={period} showInterval={false} />
        <Field label={t('admin.financeInputs.filter')}>
          {(p) => (
            <Select {...p} value={filter} onChange={(e) => setFilter(e.target.value)}>
              {(['all', 'missing', 'entered'] as const).map((f) => (
                <option key={f} value={f}>
                  {t(`admin.financeInputs.filters.${f}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.financeInputs.carrier')}>
          {(p) => (
            <Select {...p} value={carrier} onChange={(e) => setCarrier(e.target.value)}>
              <option value="">{t('admin.financeInputs.allCarriers')}</option>
              {CARRIERS.map((c) => (
                <option key={c} value={c}>
                  {t(`admin.shipping.carriers.${c}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      {q.isError ? (
        <ErrorText error={q.error} />
      ) : !q.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <div className={q.isPlaceholderData ? 'opacity-60' : undefined}>
          <p role="status" className="mb-2 text-sm text-ink-2">
            {t('admin.financeInputs.shipmentsSummary', {
              shipments: q.data.totals.shipments,
              entered: tl(q.data.totals.enteredMinor),
              enteredCount: q.data.totals.entered,
              estimated: tl(q.data.totals.estimatedMinor),
              unknown: q.data.totals.unknown,
            })}
          </p>
          {!q.data.rows.length ? (
            <EmptyState title={t('admin.financeInputs.emptyShipments')} />
          ) : (
            <Table
              label={t('admin.financeInputs.sections.shipments')}
              head={
                <tr>
                  <th className={th}>{t('admin.financeInputs.columns.date')}</th>
                  <th className={th}>{t('admin.financeInputs.columns.order')}</th>
                  <th className={th}>{t('admin.financeInputs.columns.carrier')}</th>
                  <th className={th}>{t('admin.financeInputs.columns.tracking')}</th>
                  <th className={thR}>{t('admin.financeInputs.columns.weight')}</th>
                  <th className={thR}>{t('admin.financeInputs.columns.cost')}</th>
                  {manage && (
                    <th className={th}>
                      <span className="sr-only">{t('common.actions')}</span>
                    </th>
                  )}
                </tr>
              }
            >
              {q.data.rows.map((r) => (
                <tr key={r.shipmentId} className="border-t border-line">
                  <td className={td}>{formatDate(r.shippedOn, false)}</td>
                  <td className={td}>
                    <Link
                      to="/siparisler/$id"
                      params={{ id: r.orderId }}
                      className="font-medium text-pine hover:underline"
                    >
                      {r.orderNumber}
                    </Link>
                  </td>
                  <td className={td}>{t(`admin.shipping.carriers.${r.carrier}`)}</td>
                  <td className={td}>{r.trackingNumber ?? '—'}</td>
                  <td className={tdR}>
                    {r.weightGrams
                      ? t('admin.financeInputs.kg', { kg: formatNumber(r.weightGrams / 1000, 1) })
                      : '—'}
                  </td>
                  <td className={tdR}>
                    {r.cost ? (
                      <>
                        {tl(r.cost.amountMinor)}
                        <span className="block text-xs text-ink-3">
                          {t(`admin.financeInputs.sources.${r.cost.source}`)}
                          {r.cost.reference ? ` · ${r.cost.reference}` : ''}
                        </span>
                      </>
                    ) : r.estimateMinor !== null ? (
                      <>
                        {tl(r.estimateMinor)} <Badge tone="amber">{t('admin.financeInputs.estimated')}</Badge>
                      </>
                    ) : (
                      <span className="text-ink-3">{t('admin.financeInputs.unknownCost')}</span>
                    )}
                  </td>
                  {manage && (
                    <td className={`${td} text-end`}>
                      <span className="inline-flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
                          {r.cost ? t('common.edit') : t('admin.financeInputs.enterCost')}
                        </Button>
                        {r.cost && (
                          <ConfirmButton
                            message={t('admin.financeInputs.confirmDeleteCost')}
                            onConfirm={() => remove.mutateAsync(r.cost!.id)}
                          >
                            {t('common.delete')}
                          </ConfirmButton>
                        )}
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </Table>
          )}
        </div>
      )}
      {editing && <CostDialog row={editing} onClose={() => setEditing(null)} />}
      {importing && <ImportDialog manage={manage} onClose={() => setImporting(false)} />}
    </section>
  );
}

function CostDialog({ row, onClose }: { row: ShipRow; onClose: () => void }) {
  const t = useT();
  const c = row.cost;
  const [amount, setAmount] = useState(c ? minorToInput(c.amountMinor) : '');
  const [vat, setVat] = useState(c ? minorToInput(c.vatMinor) : '');
  const [reference, setReference] = useState(c?.reference ?? '');
  const [date, setDate] = useState(c?.entryDate ?? istanbulToday());
  const [idem] = useState(() => new IdempotencyKey());
  const amountMinor = toMinor(amount, 'TRY');
  const vatMinor = vat.trim() ? toMinor(vat, 'TRY') : 0;
  const save = useApiMutation(
    () => {
      const body = {
        amountMinor: amountMinor!,
        vatMinor: vatMinor!,
        reference: reference.trim() || null,
        entryDate: date,
      };
      return c
        ? api.patch(`/v1/admin/finance/shipment-costs/${c.id}`, body, { ifMatch: c.version })
        : api.post(`/v1/admin/finance/shipments/${row.shipmentId}/cost`, body, { idempotencyKey: idem });
    },
    {
      invalidate: [['shipment-costs']],
      success: t('admin.financeInputs.costSaved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={
        c
          ? t('admin.financeInputs.editCost')
          : t('admin.financeInputs.newCost', { tracking: row.trackingNumber ?? row.orderNumber })
      }
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={amountMinor === null || vatMinor === null}
            onClick={() => save.mutate(undefined)}
          >
            {t('admin.financeInputs.saveCost')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={t('admin.financeInputs.amount')}
          required
          error={
            amount && amountMinor === null
              ? t('admin.financeInputs.amountInvalid')
              : fieldError(save.error, 'amountMinor')
          }
        >
          {(p) => (
            <Input {...p} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          )}
        </Field>
        <Field
          label={t('admin.financeInputs.vat')}
          optionalLabel={t('common.optional')}
          error={
            vat && vatMinor === null
              ? t('admin.financeInputs.amountInvalid')
              : fieldError(save.error, 'vatMinor')
          }
        >
          {(p) => <Input {...p} inputMode="decimal" value={vat} onChange={(e) => setVat(e.target.value)} />}
        </Field>
        <Field label={t('admin.financeInputs.reference')} optionalLabel={t('common.optional')}>
          {(p) => (
            <Input {...p} maxLength={100} value={reference} onChange={(e) => setReference(e.target.value)} />
          )}
        </Field>
        <Field label={t('admin.financeInputs.entryDate')} required>
          {(p) => <Input {...p} type="date" value={date} onChange={(e) => setDate(e.target.value)} />}
        </Field>
      </div>
    </Dialog>
  );
}

function ImportDialog({ manage, onClose }: { manage: boolean; onClose: () => void }) {
  const t = useT();
  const [file, setFile] = useState<File | null>(null);
  const [overwrite, setOverwrite] = useState(false);
  const [result, setResult] = useState<ShipmentCostImport | null>(null);
  const send = useApiMutation(
    (apply: boolean) => {
      const fd = new FormData();
      fd.append('file', file!);
      return api.upload<ShipmentCostImport>(
        `/v1/admin/finance/shipment-costs/import?apply=${apply}&overwrite=${overwrite}`,
        fd,
      );
    },
    {
      invalidate: [['shipment-costs']],
      onSuccess: (r) => {
        setResult(r);
        if (r.applied) toast.success(t('admin.financeInputs.importApplied', { count: r.applied }));
      },
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.financeInputs.importTitle')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.close')}</Button>
          <Button loading={send.isPending} disabled={!file} onClick={() => send.mutate(false)}>
            {t('admin.financeInputs.preview')}
          </Button>
          {manage && (
            <Button
              variant="primary"
              loading={send.isPending}
              disabled={
                !file || !result || result.summary.ready + (overwrite ? result.summary.exists : 0) === 0
              }
              onClick={() => send.mutate(true)}
            >
              {t('admin.financeInputs.apply')}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <p className="text-ink-2">{t('admin.financeInputs.importLead')}</p>
        <Field label={t('admin.financeInputs.file')}>
          {(p) => (
            <input
              {...p}
              type="file"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="block text-sm"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setResult(null);
              }}
            />
          )}
        </Field>
        {manage && (
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={overwrite}
              onChange={(e) => {
                setOverwrite(e.target.checked);
                setResult(null);
              }}
            />
            {t('admin.financeInputs.overwrite')}
          </label>
        )}
        {result && (
          <>
            <p role="status" className="font-medium">
              {t('admin.financeInputs.importSummary', {
                ready: result.summary.ready,
                exists: result.summary.exists,
                errors: result.summary.errors,
              })}
            </p>
            <div
              className="max-h-72 overflow-y-auto"
              tabIndex={0}
              role="region"
              aria-label={t('admin.financeInputs.importTitle')}
            >
              <Table
                label={t('admin.financeInputs.importTitle')}
                head={
                  <tr>
                    <th className={thR}>{t('admin.financeInputs.row')}</th>
                    <th className={th}>{t('admin.financeInputs.columns.tracking')}</th>
                    <th className={thR}>{t('admin.financeInputs.columns.amount')}</th>
                    <th className={th}>{t('admin.financeInputs.status')}</th>
                  </tr>
                }
              >
                {result.rows.map((r) => (
                  <tr key={r.row} className="border-t border-line">
                    <td className={tdR}>{r.row}</td>
                    <td className={td}>{r.trackingNumber}</td>
                    <td className={tdR}>{r.amountMinor === null ? '—' : minorToInput(r.amountMinor)}</td>
                    <td className={td}>
                      {t(`admin.financeInputs.rowStatus.${r.status}`)}
                      {r.problem && (
                        <span className="block text-xs text-ink-3">{t(`errors.${r.problem}`)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </Table>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

// ------------------------------------------------------------------------- Kargo maliyeti tahmin kuralları

type ShipRule = ShippingCostRuleList[number];

function ShippingRules({ manage }: { manage: boolean }) {
  const t = useT();
  const money = useMoney();
  const { formatNumber } = useI18n();
  const q = useQuery({
    queryKey: ['shipping-cost-rules'],
    queryFn: () => api.get<ShippingCostRuleList>('/v1/admin/finance/shipping-cost-rules'),
  });
  const [editing, setEditing] = useState<ShipRule | 'new' | null>(null);
  const remove = useApiMutation((id: string) => api.del(`/v1/admin/finance/shipping-cost-rules/${id}`), {
    invalidate: [['shipping-cost-rules'], ['shipment-costs']],
    success: t('admin.financeInputs.ruleDeleted'),
  });
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-semibold text-ink">
          {t('admin.financeInputs.sections.shippingRules')}
        </h2>
        <HelpTip k="finance_inputs.shipping_rule" />
        {manage && (
          <Button variant="primary" size="sm" className="ms-auto" onClick={() => setEditing('new')}>
            <Plus className="h-4 w-4" aria-hidden />
            {t('admin.financeInputs.addRule')}
          </Button>
        )}
      </div>
      {q.isError ? (
        <ErrorText error={q.error} />
      ) : !q.data ? (
        <Spinner label={t('common.loading')} />
      ) : !q.data.length ? (
        <EmptyState
          title={t('admin.financeInputs.emptyRules')}
          description={t('admin.financeInputs.emptyRulesLead')}
        />
      ) : (
        <Table
          label={t('admin.financeInputs.sections.shippingRules')}
          head={
            <tr>
              <th className={th}>{t('admin.financeInputs.columns.carrier')}</th>
              <th className={thR}>{t('admin.financeInputs.columns.maxWeight')}</th>
              <th className={thR}>{t('admin.financeInputs.columns.cost')}</th>
              {manage && (
                <th className={th}>
                  <span className="sr-only">{t('common.actions')}</span>
                </th>
              )}
            </tr>
          }
        >
          {q.data.map((r) => (
            <tr key={r.id} className="border-t border-line">
              <td className={td}>
                {r.carrier ? t(`admin.shipping.carriers.${r.carrier}`) : t('admin.financeInputs.allCarriers')}
                {r.note && <span className="block text-xs text-ink-3">{r.note}</span>}
              </td>
              <td className={tdR}>
                {r.maxWeightGrams
                  ? t('admin.financeInputs.kg', { kg: formatNumber(r.maxWeightGrams / 1000, 1) })
                  : t('admin.financeInputs.noLimit')}
              </td>
              <td className={tdR}>{money(r.costMinor, r.currency)}</td>
              {manage && (
                <td className={`${td} text-end`}>
                  <span className="inline-flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
                      {t('common.edit')}
                    </Button>
                    <ConfirmButton
                      message={t('admin.financeInputs.confirmDeleteRule')}
                      onConfirm={() => remove.mutateAsync(r.id)}
                    >
                      {t('common.delete')}
                    </ConfirmButton>
                  </span>
                </td>
              )}
            </tr>
          ))}
        </Table>
      )}
      {editing && <RuleDialog rule={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </section>
  );
}

/** "1,5" kg → 1500 g; boş → null; okunamazsa undefined. */
function kgToGrams(s: string): number | null | undefined {
  if (!s.trim()) return null;
  const m = /^\s*(\d{1,4})(?:[.,](\d{1,3}))?\s*$/.exec(s);
  if (!m) return undefined;
  const g = Number(m[1]) * 1000 + Number((m[2] ?? '0').padEnd(3, '0'));
  return g > 0 ? g : undefined;
}

function RuleDialog({ rule, onClose }: { rule: ShipRule | null; onClose: () => void }) {
  const t = useT();
  const [carrier, setCarrier] = useState(rule?.carrier ?? '');
  const [weight, setWeight] = useState(
    rule?.maxWeightGrams ? String(rule.maxWeightGrams / 1000).replace('.', ',') : '',
  );
  const [cost, setCost] = useState(rule ? minorToInput(rule.costMinor) : '');
  const [note, setNote] = useState(rule?.note ?? '');
  const [idem] = useState(() => new IdempotencyKey());
  const grams = kgToGrams(weight);
  const costMinor = toMinor(cost, 'TRY');
  const save = useApiMutation(
    () => {
      const body = {
        carrier: carrier || null,
        maxWeightGrams: grams ?? null,
        costMinor: costMinor!,
        note: note.trim() || null,
      };
      return rule
        ? api.patch(`/v1/admin/finance/shipping-cost-rules/${rule.id}`, body, { ifMatch: rule.version })
        : api.post('/v1/admin/finance/shipping-cost-rules', body, { idempotencyKey: idem });
    },
    {
      invalidate: [['shipping-cost-rules'], ['shipment-costs']],
      success: t('admin.financeInputs.ruleSaved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={rule ? t('admin.financeInputs.editRule') : t('admin.financeInputs.addRule')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={costMinor === null || grams === undefined}
            onClick={() => save.mutate(undefined)}
          >
            {t('admin.financeInputs.saveRule')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('admin.financeInputs.carrier')}>
          {(p) => (
            <Select {...p} value={carrier} onChange={(e) => setCarrier(e.target.value)}>
              <option value="">{t('admin.financeInputs.allCarriers')}</option>
              {CARRIERS.map((c) => (
                <option key={c} value={c}>
                  {t(`admin.shipping.carriers.${c}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label={t('admin.financeInputs.maxWeight')}
          optionalLabel={t('admin.financeInputs.noLimit')}
          error={grams === undefined ? t('admin.financeInputs.amountInvalid') : undefined}
        >
          {(p) => (
            <Input {...p} inputMode="decimal" value={weight} onChange={(e) => setWeight(e.target.value)} />
          )}
        </Field>
        <Field
          label={t('admin.financeInputs.cost')}
          required
          error={
            cost && costMinor === null
              ? t('admin.financeInputs.amountInvalid')
              : fieldError(save.error, 'costMinor')
          }
        >
          {(p) => <Input {...p} inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} />}
        </Field>
        <Field label={t('admin.financeInputs.note')} optionalLabel={t('common.optional')}>
          {(p) => <Input {...p} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />}
        </Field>
      </div>
    </Dialog>
  );
}

// -------------------------------------------------------------------------------------- İşletme giderleri

type Expense = ExpenseList['rows'][number];

function Expenses({ manage }: { manage: boolean }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { stores } = useStoreChoice();
  const period = usePeriod('this_month');
  const [category, setCategory] = useState('');
  const [editing, setEditing] = useState<Expense | 'new' | null>(null);
  const params = new URLSearchParams(period.params);
  params.delete('interval');
  if (category) params.set('category', category);
  const q = useQuery({
    queryKey: ['expenses', params.toString()],
    queryFn: () => api.get<ExpenseList>(`/v1/admin/finance/expenses?${params}`),
    placeholderData: keepPreviousData,
  });
  const remove = useApiMutation((id: string) => api.del(`/v1/admin/finance/expenses/${id}`), {
    invalidate: [['expenses']],
    success: t('admin.financeInputs.expenseDeleted'),
  });
  const storeName = (id: string | null) =>
    id ? (stores.find((s) => s.id === id)?.name ?? '—') : t('admin.financeInputs.noStore');
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-semibold text-ink">{t('admin.financeInputs.sections.expenses')}</h2>
        <HelpTip k="finance_inputs.expense" />
        {manage && (
          <Button variant="primary" size="sm" className="ms-auto" onClick={() => setEditing('new')}>
            <Plus className="h-4 w-4" aria-hidden />
            {t('admin.financeInputs.addExpense')}
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <PeriodControls period={period} showInterval={false} />
        <Field label={t('admin.financeInputs.category')}>
          {(p) => (
            <Select {...p} value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">{t('admin.financeInputs.allCategories')}</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {t(`admin.financeInputs.categories.${c}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      {q.isError ? (
        <ErrorText error={q.error} />
      ) : !q.data ? (
        <Spinner label={t('common.loading')} />
      ) : !q.data.rows.length ? (
        <EmptyState
          title={t('admin.financeInputs.emptyExpenses')}
          description={t('admin.financeInputs.emptyExpensesLead')}
        />
      ) : (
        <div className={q.isPlaceholderData ? 'opacity-60' : undefined}>
          <p role="status" className="mb-2 text-sm text-ink-2">
            {t('admin.financeInputs.expensesTotal', {
              amount: money(q.data.totals.amountMinor, 'TRY'),
              vat: money(q.data.totals.vatMinor, 'TRY'),
            })}
          </p>
          <Table
            label={t('admin.financeInputs.sections.expenses')}
            head={
              <tr>
                <th className={th}>{t('admin.financeInputs.columns.date')}</th>
                <th className={th}>{t('admin.financeInputs.columns.category')}</th>
                <th className={th}>{t('admin.financeInputs.columns.description')}</th>
                <th className={thR}>{t('admin.financeInputs.columns.amount')}</th>
                <th className={thR}>{t('admin.financeInputs.columns.vat')}</th>
                <th className={th}>{t('admin.financeInputs.columns.store')}</th>
                {manage && (
                  <th className={th}>
                    <span className="sr-only">{t('common.actions')}</span>
                  </th>
                )}
              </tr>
            }
          >
            {q.data.rows.map((r) => (
              <tr key={r.id} className="border-t border-line">
                <td className={td}>{formatDate(r.expenseDate, false)}</td>
                <td className={td}>{t(`admin.financeInputs.categories.${r.category}`)}</td>
                <td className={td}>
                  {r.description}
                  {r.reference && <span className="block text-xs text-ink-3">{r.reference}</span>}
                </td>
                <td className={tdR}>{money(r.amountMinor, r.currency)}</td>
                <td className={tdR}>{money(r.vatMinor, r.currency)}</td>
                <td className={td}>{storeName(r.storeId)}</td>
                {manage && (
                  <td className={`${td} text-end`}>
                    <span className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
                        {t('common.edit')}
                      </Button>
                      <ConfirmButton
                        message={t('admin.financeInputs.confirmDeleteExpense')}
                        onConfirm={() => remove.mutateAsync(r.id)}
                      >
                        {t('common.delete')}
                      </ConfirmButton>
                    </span>
                  </td>
                )}
              </tr>
            ))}
          </Table>
        </div>
      )}
      {editing && (
        <ExpenseDialog expense={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
    </section>
  );
}

function ExpenseDialog({ expense, onClose }: { expense: Expense | null; onClose: () => void }) {
  const t = useT();
  const { stores } = useStoreChoice();
  const [date, setDate] = useState(expense?.expenseDate ?? istanbulToday());
  const [category, setCategory] = useState<string>(expense?.category ?? 'other');
  const [description, setDescription] = useState(expense?.description ?? '');
  const [amount, setAmount] = useState(expense ? minorToInput(expense.amountMinor) : '');
  const [vat, setVat] = useState(expense ? minorToInput(expense.vatMinor) : '');
  const [storeId, setStoreId] = useState(expense?.storeId ?? '');
  const [reference, setReference] = useState(expense?.reference ?? '');
  const [idem] = useState(() => new IdempotencyKey());
  const amountMinor = toMinor(amount, 'TRY');
  const vatMinor = vat.trim() ? toMinor(vat, 'TRY') : 0;
  const save = useApiMutation(
    () => {
      const body = {
        expenseDate: date,
        category,
        description: description.trim(),
        amountMinor: amountMinor!,
        vatMinor: vatMinor!,
        storeId: storeId || null,
        reference: reference.trim() || null,
      };
      return expense
        ? api.patch(`/v1/admin/finance/expenses/${expense.id}`, body, { ifMatch: expense.version })
        : api.post('/v1/admin/finance/expenses', body, { idempotencyKey: idem });
    },
    {
      invalidate: [['expenses']],
      success: t('admin.financeInputs.expenseSaved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={expense ? t('admin.financeInputs.editExpense') : t('admin.financeInputs.addExpense')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!amountMinor || vatMinor === null || description.trim().length < 2}
            onClick={() => save.mutate(undefined)}
          >
            {t('admin.financeInputs.saveExpense')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('admin.financeInputs.expenseDate')} required>
          {(p) => <Input {...p} type="date" value={date} onChange={(e) => setDate(e.target.value)} />}
        </Field>
        <Field label={t('admin.financeInputs.category')} required>
          {(p) => (
            <Select {...p} value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {t(`admin.financeInputs.categories.${c}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <div className="sm:col-span-2">
          <Field
            label={t('admin.financeInputs.description')}
            required
            error={fieldError(save.error, 'description')}
          >
            {(p) => (
              <Input
                {...p}
                maxLength={300}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            )}
          </Field>
        </div>
        <Field
          label={t('admin.financeInputs.amount')}
          required
          error={
            amount && amountMinor === null
              ? t('admin.financeInputs.amountInvalid')
              : fieldError(save.error, 'amountMinor')
          }
        >
          {(p) => (
            <Input {...p} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          )}
        </Field>
        <Field
          label={t('admin.financeInputs.vat')}
          optionalLabel={t('common.optional')}
          error={
            vat && vatMinor === null
              ? t('admin.financeInputs.amountInvalid')
              : fieldError(save.error, 'vatMinor')
          }
        >
          {(p) => <Input {...p} inputMode="decimal" value={vat} onChange={(e) => setVat(e.target.value)} />}
        </Field>
        <Field label={t('admin.financeInputs.store')}>
          {(p) => (
            <Select {...p} value={storeId} onChange={(e) => setStoreId(e.target.value)}>
              <option value="">{t('admin.financeInputs.noStore')}</option>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.financeInputs.reference')} optionalLabel={t('common.optional')}>
          {(p) => (
            <Input {...p} maxLength={100} value={reference} onChange={(e) => setReference(e.target.value)} />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
